import React from 'react';

export type StaticTableColumn<T> = {
    /** Stable identifier for the column. */
    key: string;
    /** Header cell content. */
    header: React.ReactNode;
    /** Cell content for a given row. */
    render: (row: T, index: number) => React.ReactNode;
    /** Optional alignment; defaults to left. */
    align?: 'left' | 'center' | 'right';
    /** Optional fixed width (e.g. '60px'). */
    width?: string;
    /** Extra class applied to both header and body cells in this column. */
    className?: string;
};

type StaticTableProps<T> = {
    columns: StaticTableColumn<T>[];
    rows: T[];
    /** Derive a stable React key per row; defaults to the row index. */
    getRowKey?: (row: T, index: number) => React.Key;
    /** Message shown when there are no rows. */
    emptyText?: string;
    /** Render shimmer placeholder rows instead of the data. */
    loading?: boolean;
    /** Number of placeholder rows shown while loading (default 3). */
    loadingRows?: number;
    className?: string;
};

const StaticTable = <T,>({
    columns,
    rows,
    getRowKey,
    emptyText = 'No records',
    loading = false,
    loadingRows = 3,
    className,
}: StaticTableProps<T>): JSX.Element => (
    <table className={`v2-static-table${className ? ` ${className}` : ''}`}>
        <thead>
            <tr>
                {columns.map(col => (
                    <th
                        key={col.key}
                        className={col.className}
                        style={{ width: col.width, textAlign: col.align }}
                    >
                        {col.header}
                    </th>
                ))}
            </tr>
        </thead>
        <tbody>
            {loading ? (
                Array.from({ length: loadingRows }, (_v, r) => (
                    <tr
                        key={`loading-${r}`}
                        className="v2-static-table-loading"
                    >
                        {columns.map(col => (
                            <td
                                key={col.key}
                                style={{ textAlign: col.align }}
                            >
                                <span className="skeleton-shimmer v2-static-table-loading-bar" />
                            </td>
                        ))}
                    </tr>
                ))
            ) : rows.length === 0 ? (
                <tr className="v2-static-table-empty">
                    <td colSpan={columns.length}>{emptyText}</td>
                </tr>
            ) : (
                rows.map((row, i) => (
                    <tr key={getRowKey ? getRowKey(row, i) : i}>
                        {columns.map(col => (
                            <td
                                key={col.key}
                                className={col.className}
                                style={{ textAlign: col.align }}
                            >
                                {col.render(row, i)}
                            </td>
                        ))}
                    </tr>
                ))
            )}
        </tbody>
    </table>
);

export default StaticTable;
